import * as api from "../api/index";

export const SUBSCRIPTION_ACTIONS = {
  SET_SELECTED_PACKAGE: "SUBSCRIPTION/SET_SELECTED_PACKAGE",
  SET_USER_COUNT: "SUBSCRIPTION/SET_USER_COUNT",
  SET_BILLING_TYPE: "SUBSCRIPTION/SET_BILLING_TYPE",
  SET_DISCOUNT_CODE: "SUBSCRIPTION/SET_DISCOUNT_CODE",
  SET_APPLIED_DISCOUNT: "SUBSCRIPTION/SET_APPLIED_DISCOUNT",
  VALIDATE_DISCOUNT_START: "SUBSCRIPTION/VALIDATE_DISCOUNT_START",
  VALIDATE_DISCOUNT_SUCCESS: "SUBSCRIPTION/VALIDATE_DISCOUNT_SUCCESS",
  VALIDATE_DISCOUNT_ERROR: "SUBSCRIPTION/VALIDATE_DISCOUNT_ERROR",
  CLEAR_DISCOUNT: "SUBSCRIPTION/CLEAR_DISCOUNT",
  SET_CALCULATED_PRICING: "SUBSCRIPTION/SET_CALCULATED_PRICING",
  CALCULATE_PRICING: "SUBSCRIPTION/CALCULATE_PRICING",
  SET_REGISTRATION_DATA: "SUBSCRIPTION/SET_REGISTRATION_DATA",
  CLEAR_REGISTRATION_DATA: "SUBSCRIPTION/CLEAR_REGISTRATION_DATA",
  START_SUBSCRIPTION_FLOW: "SUBSCRIPTION/START_SUBSCRIPTION_FLOW",
  COMPLETE_SUBSCRIPTION_FLOW: "SUBSCRIPTION/COMPLETE_SUBSCRIPTION_FLOW",
  CLEAR_SUBSCRIPTION_DATA: "SUBSCRIPTION/CLEAR_SUBSCRIPTION_DATA",
  SET_LOADING: "SUBSCRIPTION/SET_LOADING",
  SET_ERROR: "SUBSCRIPTION/SET_ERROR",
  CLEAR_ERROR: "SUBSCRIPTION/CLEAR_ERROR",
  FETCH_MY_SUBSCRIPTION_START: "SUBSCRIPTION/FETCH_MY_SUBSCRIPTION_START",
  FETCH_MY_SUBSCRIPTION_SUCCESS: "SUBSCRIPTION/FETCH_MY_SUBSCRIPTION_SUCCESS",
  FETCH_MY_SUBSCRIPTION_ERROR: "SUBSCRIPTION/FETCH_MY_SUBSCRIPTION_ERROR",
  CANCEL_SUBSCRIPTION_START: "SUBSCRIPTION/CANCEL_SUBSCRIPTION_START",
  CANCEL_SUBSCRIPTION_SUCCESS: "SUBSCRIPTION/CANCEL_SUBSCRIPTION_SUCCESS",
  CANCEL_SUBSCRIPTION_ERROR: "SUBSCRIPTION/CANCEL_SUBSCRIPTION_ERROR",
  FETCH_BILLING_HISTORY_START: "SUBSCRIPTION/FETCH_BILLING_HISTORY_START",
  FETCH_BILLING_HISTORY_SUCCESS: "SUBSCRIPTION/FETCH_BILLING_HISTORY_SUCCESS",
  FETCH_BILLING_HISTORY_ERROR: "SUBSCRIPTION/FETCH_BILLING_HISTORY_ERROR",
  CHECK_SUBSCRIPTION_STATUS_START:
    "SUBSCRIPTION/CHECK_SUBSCRIPTION_STATUS_START",
  CHECK_SUBSCRIPTION_STATUS_SUCCESS:
    "SUBSCRIPTION/CHECK_SUBSCRIPTION_STATUS_SUCCESS",
  CHECK_SUBSCRIPTION_STATUS_ERROR:
    "SUBSCRIPTION/CHECK_SUBSCRIPTION_STATUS_ERROR",
};

const STORAGE_KEY = "subscriptionFlow";

const getStateSlice = (getState) => getState()?.subscription || {};

const persistState = (state) => {
  if (typeof window === "undefined") return;

  const dataToSave = {
    selectedPackage: state.selectedPackage || null,
    userCount: state.userCount || 1,
    billingType: state.billingType || "monthly",
    discountCode: state.discountCode || null,
    appliedDiscount: state.appliedDiscount || null,
    registrationData: state.registrationData || null,
    flowStep: state.flowStep || "package_selection",
  };

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
};

const clearPersistedState = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
};

const calculateSubscriptionPricing = (state) => {
  const selectedPackage = state.selectedPackage;
  const userCount = Math.max(1, Number(state.userCount) || 1);
  const billingType = state.billingType || "monthly";
  const appliedDiscount = state.appliedDiscount;

  if (!selectedPackage) {
    return {
      subtotal: 0,
      discount: 0,
      total: 0,
      basePrice: 0,
      userCount,
    };
  }

  const baseUnitPrice = Number(
    billingType === "yearly"
      ? selectedPackage.basePricePerYear || 0
      : selectedPackage.basePricePerMonth || 0,
  );

  let subtotal =
    selectedPackage.pricingModel === "per_user"
      ? baseUnitPrice * userCount
      : baseUnitPrice;

  if (
    selectedPackage.pricingModel === "per_user" &&
    Array.isArray(selectedPackage.volumeDiscounts)
  ) {
    const applicableVolumeDiscount = [...selectedPackage.volumeDiscounts]
      .filter((discount) => userCount >= Number(discount.minUsers || 0))
      .sort((a, b) => Number(b.minUsers || 0) - Number(a.minUsers || 0))[0];

    if (applicableVolumeDiscount?.discountPercentage) {
      subtotal =
        subtotal *
        (1 - Number(applicableVolumeDiscount.discountPercentage || 0) / 100);
    }
  }

  const normalizedSubtotal = Math.round(subtotal * 100) / 100;

  let discountAmount = 0;
  if (appliedDiscount) {
    if (appliedDiscount.discountType === "percentage") {
      discountAmount =
        normalizedSubtotal * (Number(appliedDiscount.discountValue || 0) / 100);
    } else {
      discountAmount = Number(appliedDiscount.discountValue || 0);
    }
  }

  discountAmount = Math.min(
    normalizedSubtotal,
    Math.round(discountAmount * 100) / 100,
  );
  const total = Math.max(
    0,
    Math.round((normalizedSubtotal - discountAmount) * 100) / 100,
  );

  return {
    subtotal: normalizedSubtotal,
    discount: discountAmount,
    total,
    basePrice: baseUnitPrice,
    userCount,
  };
};

const normalizeSubscriptionsPayload = (data) => {
  const rawSubscriptions = data?.subscription ?? data;

  if (Array.isArray(rawSubscriptions)) {
    return rawSubscriptions;
  }

  if (rawSubscriptions && typeof rawSubscriptions === "object") {
    return [rawSubscriptions];
  }

  return [];
};

const normalizeSubscriptionPayload = (data) => {
  const subscriptions = normalizeSubscriptionsPayload(data);
  return subscriptions[0] || null;
};

const normalizeBillingHistoryPayload = (data) => {
  const subscriptions = normalizeSubscriptionsPayload(data);
  const subscription = subscriptions[0] || null;
  const paymentHistory = subscriptions
    .flatMap((entry) =>
      (entry?.paymentHistory || []).map((payment) => ({
        ...payment,
        subscriptionId: entry?._id || payment?.subscriptionId || null,
        subscriptionPackageName:
          entry?.packageName || payment?.subscriptionPackageName || null,
        subscriptionCompanyId:
          entry?.companyId || payment?.subscriptionCompanyId || null,
      })),
    )
    .sort(
      (left, right) =>
        new Date(right?.paidAt || 0).getTime() -
        new Date(left?.paidAt || 0).getTime(),
    );

  return {
    subscription,
    subscriptions,
    paymentHistory,
  };
};

export const setSelectedPackage = (pkg) => ({
  type: SUBSCRIPTION_ACTIONS.SET_SELECTED_PACKAGE,
  payload: pkg,
});

export const setUserCount = (count) => ({
  type: SUBSCRIPTION_ACTIONS.SET_USER_COUNT,
  payload: count,
});

export const setBillingType = (billingType) => ({
  type: SUBSCRIPTION_ACTIONS.SET_BILLING_TYPE,
  payload: billingType,
});

export const setDiscountCode = (code) => ({
  type: SUBSCRIPTION_ACTIONS.SET_DISCOUNT_CODE,
  payload: code,
});

export const setAppliedDiscount = (discount) => ({
  type: SUBSCRIPTION_ACTIONS.SET_APPLIED_DISCOUNT,
  payload: discount,
});

export const clearDiscount = () => ({
  type: SUBSCRIPTION_ACTIONS.CLEAR_DISCOUNT,
});

export const setCalculatedPricing = (pricing) => ({
  type: SUBSCRIPTION_ACTIONS.SET_CALCULATED_PRICING,
  payload: pricing,
});

export const setRegistrationData = (data) => ({
  type: SUBSCRIPTION_ACTIONS.SET_REGISTRATION_DATA,
  payload: data,
});

export const clearRegistrationData = () => ({
  type: SUBSCRIPTION_ACTIONS.CLEAR_REGISTRATION_DATA,
});

export const startSubscriptionFlow = (pkg) => ({
  type: SUBSCRIPTION_ACTIONS.START_SUBSCRIPTION_FLOW,
  payload: pkg,
});

export const completeSubscriptionFlow = (result) => ({
  type: SUBSCRIPTION_ACTIONS.COMPLETE_SUBSCRIPTION_FLOW,
  payload: result,
});

export const clearSubscriptionData = () => ({
  type: SUBSCRIPTION_ACTIONS.CLEAR_SUBSCRIPTION_DATA,
});

export const setLoading = (isLoading) => ({
  type: SUBSCRIPTION_ACTIONS.SET_LOADING,
  payload: isLoading,
});

export const setError = (message) => ({
  type: SUBSCRIPTION_ACTIONS.SET_ERROR,
  payload: message,
});

export const clearError = () => ({
  type: SUBSCRIPTION_ACTIONS.CLEAR_ERROR,
});

export const calculatePricing = () => (dispatch, getState) => {
  const subscriptionState = getStateSlice(getState);
  const pricing = calculateSubscriptionPricing(subscriptionState);
  dispatch(setCalculatedPricing(pricing));
  return pricing;
};

export const validateDiscountCode = (codeData) => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_START });

  try {
    const { data } = await api.validateDiscountCode(codeData);

    if (data?.success) {
      const discount = data?.data || data?.discount || null;
      dispatch({
        type: SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_SUCCESS,
        payload: discount,
      });
      return { success: true, data: discount };
    }

    const message = data?.message || "Failed to validate discount code";
    dispatch({
      type: SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_ERROR,
      payload: message,
    });
    return { success: false, message };
  } catch (error) {
    const message =
      error?.response?.data?.message || "Failed to validate discount code";
    dispatch({
      type: SUBSCRIPTION_ACTIONS.VALIDATE_DISCOUNT_ERROR,
      payload: message,
    });
    return { success: false, message };
  }
};

export const saveToLocalStorage = () => (dispatch, getState) => {
  persistState(getStateSlice(getState));
  return true;
};

export const loadFromLocalStorage = () => (dispatch) => {
  if (typeof window === "undefined") return null;

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);

    if (parsed.selectedPackage) {
      dispatch(setSelectedPackage(parsed.selectedPackage));
    }
    if (parsed.userCount) {
      dispatch(setUserCount(parsed.userCount));
    }
    if (parsed.billingType) {
      dispatch(setBillingType(parsed.billingType));
    }
    if (parsed.discountCode) {
      dispatch(setDiscountCode(parsed.discountCode));
    }
    if (parsed.appliedDiscount) {
      dispatch(setAppliedDiscount(parsed.appliedDiscount));
    }
    if (parsed.registrationData) {
      dispatch(setRegistrationData(parsed.registrationData));
    }

    dispatch(calculatePricing());
    return parsed;
  } catch (error) {
    clearPersistedState();
    return null;
  }
};

export const clearLocalStorage = () => (dispatch) => {
  clearPersistedState();
  dispatch(clearSubscriptionData());
  return true;
};

export const initializeSubscriptionFlow =
  (packageData, initialUserCount = 1, initialBillingType = "monthly") =>
  (dispatch) => {
    dispatch(startSubscriptionFlow(packageData));
    dispatch(setSelectedPackage(packageData));
    dispatch(setUserCount(initialUserCount));
    dispatch(setBillingType(initialBillingType));
    dispatch(calculatePricing());
    return true;
  };

export const updateUserCountAndRecalculate = (count) => (dispatch) => {
  dispatch(setUserCount(count));
  dispatch(calculatePricing());
};

export const updateBillingTypeAndRecalculate = (billingType) => (dispatch) => {
  dispatch(setBillingType(billingType));
  dispatch(calculatePricing());
};

export const fetchMySubscription = () => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_START });

  try {
    const { data } = await api.getMySubscription();
    const subscriptions = normalizeSubscriptionsPayload(data?.data);
    const subscription = normalizeSubscriptionPayload(data?.data);

    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_SUCCESS,
      payload: {
        currentSubscription: subscription,
        subscriptions,
      },
    });

    return {
      success: true,
      data: subscription,
      subscriptions,
      stripeSubscription: data?.data?.stripeSubscription,
    };
  } catch (error) {
    const message =
      error?.response?.data?.message || "Failed to fetch subscription";
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_MY_SUBSCRIPTION_ERROR,
      payload: message,
    });
    return { success: false, message };
  }
};

export const cancelSubscription =
  (subscriptionId, cancellationData) => async (dispatch) => {
    dispatch({ type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_START });

    try {
      const { data } = await api.cancelSubscription(
        subscriptionId,
        cancellationData,
      );
      const subscription = normalizeSubscriptionPayload(data?.data);

      dispatch({
        type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_SUCCESS,
        payload: subscription,
      });

      return { success: true, data: subscription, message: data?.message };
    } catch (error) {
      const message =
        error?.response?.data?.message || "Failed to cancel subscription";
      dispatch({
        type: SUBSCRIPTION_ACTIONS.CANCEL_SUBSCRIPTION_ERROR,
        payload: message,
      });
      return { success: false, message };
    }
  };

export const fetchBillingHistory = () => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_START });

  try {
    const { data } = await api.getMySubscription();
    const billingHistory = normalizeBillingHistoryPayload(data?.data);

    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_SUCCESS,
      payload: billingHistory,
    });

    return { success: true, data: billingHistory };
  } catch (error) {
    const message =
      error?.response?.data?.message || "Failed to fetch billing history";
    dispatch({
      type: SUBSCRIPTION_ACTIONS.FETCH_BILLING_HISTORY_ERROR,
      payload: message,
    });
    return { success: false, message };
  }
};

export const checkSubscriptionStatus = () => async (dispatch) => {
  dispatch({ type: SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_START });

  try {
    const { data } = await api.getMySubscription();
    const subscription = normalizeSubscriptionPayload(data?.data);
    const status = subscription?.status || null;
    const hasSubscription = Boolean(subscription);

    dispatch({
      type: SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_SUCCESS,
      payload: {
        hasSubscription,
        status,
        subscription,
      },
    });

    return {
      success: true,
      hasSubscription,
      status,
      subscription,
    };
  } catch (error) {
    const statusCode = error?.response?.status;

    if (statusCode === 404) {
      dispatch({
        type: SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_SUCCESS,
        payload: {
          hasSubscription: false,
          status: null,
          subscription: null,
        },
      });

      return {
        success: true,
        hasSubscription: false,
        status: null,
        subscription: null,
      };
    }

    const message =
      error?.response?.data?.message || "Failed to check subscription status";
    dispatch({
      type: SUBSCRIPTION_ACTIONS.CHECK_SUBSCRIPTION_STATUS_ERROR,
      payload: message,
    });
    return { success: false, message };
  }
};

export const fetchAvailablePlans = (currentPackageId) => async () => {
  try {
    const { data } = await api.getAvailablePlans(currentPackageId);
    return {
      success: Boolean(data?.success),
      data: data?.data,
      message: data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to fetch available plans",
    };
  }
};

export const previewPlanChange = (subscriptionId, changeData) => async () => {
  try {
    const { data } = await api.previewPlanChange(subscriptionId, changeData);
    return {
      success: Boolean(data?.success),
      data: data?.data,
      message: data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message:
        error?.response?.data?.message || "Failed to preview plan change",
    };
  }
};

export const createPlanChangePaymentIntent =
  (subscriptionId, changeData) => async () => {
    try {
      const { data } = await api.createPlanChangePaymentIntent(
        subscriptionId,
        changeData,
      );
      return {
        success: Boolean(data?.success),
        data: data?.data,
        message: data?.message,
      };
    } catch (error) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          "Failed to create payment intent for plan change",
      };
    }
  };

export const changePlan = (subscriptionId, changeData) => async () => {
  try {
    const { data } = await api.changePlan(subscriptionId, changeData);
    return {
      success: Boolean(data?.success),
      data: data?.data,
      message: data?.message,
    };
  } catch (error) {
    return {
      success: false,
      message: error?.response?.data?.message || "Failed to change plan",
    };
  }
};

export const updateSubscriptionUserCount =
  (subscriptionId, userCountData) => async () => {
    try {
      const { data } = await api.updateUserCount(subscriptionId, userCountData);
      return {
        success: Boolean(data?.success),
        data: data?.data,
        message: data?.message,
      };
    } catch (error) {
      return {
        success: false,
        message:
          error?.response?.data?.message || "Failed to update user count",
      };
    }
  };
